import { useCallback } from "react";

import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  BILLING_VALID_TABS,
  type BillingTab,
  UnifiedBillingView,
  type BillingSubSearch,
  validateBillingSubSearch,
} from "@/components/billing/unified-billing-view";

interface BillingSearch extends BillingSubSearch {
  tab: BillingTab;
}

/**
 * Business workspace billing alias. Renders the same UnifiedBillingView
 * as user/billing IN PLACE — no redirect — preserving workspace context.
 */
export const Route = createFileRoute("/_authenticated/account/business/$businessId/billing")({
  validateSearch: (search: Record<string, unknown>): BillingSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    return {
      tab: BILLING_VALID_TABS.includes(raw as BillingTab) ? (raw as BillingTab) : "subscription",
      ...validateBillingSubSearch(search),
    };
  },
  component: BusinessBillingPage,
});

function BusinessBillingPage() {
  const { businessId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const setTab = useCallback(
    (next: BillingTab) =>
      navigate({
        to: "/account/business/$businessId/billing",
        params: { businessId },
        search: { tab: next },
      }),
    [navigate, businessId],
  );
  return (
    <>
      <UnifiedBillingView
        activeTab={tab}
        onTabChange={setTab}
        workspace={{ entityType: "business", entityId: businessId }}
      />
      <AccountSecurityBanner />
    </>
  );
}
